/**
 * MemorialBadgeAdmin — admin-only toggle card for Memorial Verification
 * Badges. Rendered on `/celebrate/:slug` for super-admins.
 *
 * The `tree_connected` badge is auto-derived from `family_tree_slug` on
 * the backend, so it is displayed as read-only here — flipping it
 * requires the "Link to Family Tree" flow.
 */
import { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const MANUAL_BADGES = [
  { key: "family_verified",   emoji: "✅", label: "Family Verified",   hint: "Confirmed by an immediate family member (proof of relationship on file)." },
  { key: "document_verified", emoji: "📜", label: "Document Verified", hint: "Backed by official records — birth / death / burial / press obituary." },
  { key: "historical",        emoji: "🏛", label: "Historical",        hint: "Documented historical or public figure; maintained editorially." },
];

export default function MemorialBadgeAdmin({ memorial, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const vs = memorial?.verifications || {};

  const toggle = async (key, next) => {
    if (saving) return;
    setSaving(true);
    try {
      const payload = { [key]: next };
      if (note.trim()) payload.note = note.trim();
      const res = await http.patch(
        `/memorials/${memorial.slug || memorial.id}/verifications`,
        payload,
      );
      const data = res.data || res;
      toast.success(`${next ? "Granted" : "Removed"} ${MANUAL_BADGES.find(b => b.key === key)?.label}`);
      onUpdated?.({
        verifications: data.verifications || {},
        verification_badges: data.verification_badges || [],
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update badge");
    } finally {
      setSaving(false);
    }
  };

  const treeConnected = !!memorial?.family_tree_slug;

  return (
    <div
      className="wofy-card mt-4"
      data-testid="memorial-badge-admin"
      style={{ borderColor: "var(--wofy-border)" }}
    >
      <div className="flex items-center gap-2 mb-2">
        <ShieldCheck className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
        <div className="overline" style={{ color: "var(--wofy-primary)" }}>ADMIN · MEMORIAL BADGES</div>
      </div>
      <p className="text-xs mb-3" style={{ color: "var(--wofy-text-soft)" }}>
        Toggle each verification level after reviewing supporting evidence.
        Optional note is logged in the admin audit trail.
      </p>
      <div className="space-y-2">
        {MANUAL_BADGES.map((b) => {
          const on = !!vs[b.key];
          return (
            <div key={b.key} className="flex items-start justify-between gap-3 border-t pt-2 first:border-t-0 first:pt-0" style={{ borderColor: "var(--wofy-border)" }}>
              <div className="min-w-0">
                <div className="text-sm font-medium">
                  <span className="mr-1" aria-hidden="true">{b.emoji}</span>
                  {b.label}
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>
                  {b.hint}
                </div>
                {on && vs[`${b.key}_by`] && (
                  <div className="text-[10px] font-mono mt-1 opacity-70">
                    granted by {vs[`${b.key}_by`]}
                    {vs[`${b.key}_at`] ? ` · ${new Date(vs[`${b.key}_at`]).toLocaleDateString()}` : ""}
                  </div>
                )}
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={() => toggle(b.key, !on)}
                data-testid={`badge-toggle-${b.key}`}
                className={on ? "wofy-btn-primary text-xs px-3 py-1" : "wofy-btn-ghost text-xs px-3 py-1"}
              >
                {on ? "Revoke" : "Grant"}
              </button>
            </div>
          );
        })}
        <div className="flex items-start justify-between gap-3 border-t pt-2" style={{ borderColor: "var(--wofy-border)" }}>
          <div className="min-w-0">
            <div className="text-sm font-medium">
              <span className="mr-1" aria-hidden="true">🌳</span>
              Connected to Family Tree
            </div>
            <div className="text-[11px] mt-0.5" style={{ color: "var(--wofy-text-soft)" }}>
              Auto-set when the memorial is linked to a family tree. Use the "Link to family tree" action to change this.
            </div>
          </div>
          <span
            className="text-[11px] px-2 py-0.5 rounded-full border"
            data-testid="badge-status-tree_connected"
            style={{
              borderColor: treeConnected ? "#166534" : "var(--wofy-border)",
              color: treeConnected ? "#166534" : "var(--wofy-text-soft)",
            }}
          >
            {treeConnected ? "On" : "Off"}
          </span>
        </div>
      </div>
      <label className="mt-3 block text-[11px]" style={{ color: "var(--wofy-text-soft)" }}>
        Optional audit note
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Death certificate on file (case #123)"
          data-testid="badge-admin-note"
          className="mt-1 w-full text-xs border rounded-lg px-2 py-1"
          style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
        />
      </label>
    </div>
  );
}
